import { z } from 'zod';

import { GraphQLResponse } from '../../../sdks/tableau/apis/metadataApi.js';
import {
  DatasourceModelResponse,
  MetadataResponse,
} from '../../../sdks/tableau/apis/vizqlDataServiceApi.js';

export const fieldSchema = z
  .object({
    name: z.string(),
    columnClass: z.string(),
    logicalTableId: z.string().nullable(),
    dataType: z.string().nullable(),
    defaultAggregation: z.string().nullable(),
    description: z.string().nullable(),
    descriptionInherited: z
      .array(z.object({ attribute: z.string(), value: z.string().nullable() }).nullable())
      .nullable(),
    dataCategory: z.string().nullable(),
    role: z.string().nullable(),
    defaultFormat: z.string().nullable(),
    semanticRole: z.string().nullable(),
    aggregation: z.string().nullable(),
    aggregationParam: z.string().nullable(),
    formula: z.string().nullable(),
    isAutoGenerated: z.boolean().nullable(),
    hasUserReference: z.boolean().nullable(),
    binSize: z.number().nullable(),
  })
  .partial();

export const logicalTableGroupSchema = z.object({
  logicalTableId: z.string().nullable(),
  fields: z.array(fieldSchema),
});

export const parameterSchema = z
  .object({
    name: z.string(),
    parameterType: z.string(),
    dataType: z.string().nullable(),
    value: z.union([z.number(), z.string(), z.boolean(), z.null()]),
    members: z.array(
      z.union([z.number(), z.string(), z.boolean(), z.null()]).or(
        z.object({
          value: z.union([z.number(), z.string(), z.boolean(), z.null()]),
          alias: z.string().optional(),
        }),
      ),
    ),
    min: z.number().nullable(),
    max: z.number().nullable(),
    step: z.number().nullable(),
    minDate: z.string().nullable(),
    maxDate: z.string().nullable(),
    periodValue: z.number().nullable(),
    periodType: z.string().nullable(),
  })
  .partial();

export const datasourceModelSchema = z.object({
  logicalTables: z.array(
    z.object({
      logicalTableId: z.string(),
      caption: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
    }),
  ),
  logicalTableRelationships: z.array(
    z.object({
      fromLogicalTable: z.object({
        logicalTableId: z.string(),
      }),
      toLogicalTable: z.object({
        logicalTableId: z.string(),
      }),
      expression: z
        .object({
          op: z.string().optional(),
          relationships: z.array(
            z.object({
              operator: z.string(),
              fromField: z.string(),
              toField: z.string(),
            }),
          ),
        })
        .optional(),
    }),
  ),
});

export const fieldsResultSchema = z.object({
  datasourceDescription: z.string(),
  datasourceModel: datasourceModelSchema.optional(),
  fieldGroups: z.array(logicalTableGroupSchema),
  parameters: z.array(parameterSchema),
});

type Parameter = z.infer<typeof parameterSchema>;
type Field = z.infer<typeof fieldSchema>;
type LogicalTableGroup = z.infer<typeof logicalTableGroupSchema>;
export type FieldsResult = z.infer<typeof fieldsResultSchema>;

export function simplifyReadMetadataResult(
  readMetadataResult: MetadataResponse,
  datasourceModelResult?: DatasourceModelResponse,
): FieldsResult {
  const simplifiedResponse: FieldsResult = {
    datasourceDescription: '',
    ...(datasourceModelResult
      ? { datasourceModel: getSimplifiedDatasourceModel(datasourceModelResult) }
      : {}),
    fieldGroups: [],
    parameters: [],
  };

  if (!readMetadataResult.data) {
    return simplifiedResponse;
  }

  // This is a simplified response that attempts to reduce tokens by
  // only including essential fields and renaming properties.
  const fields: Field[] = [];
  for (const field of readMetadataResult.data) {
    const toPush: Field = {
      name: field.fieldCaption,
      dataType: field.dataType,
      columnClass: field.columnClass,
      logicalTableId: normalizeLogicalTableId(field.logicalTableId),
    };

    if (field.defaultAggregation) {
      toPush.defaultAggregation = field.defaultAggregation;
    }

    if (field.formula) {
      toPush.formula = field.formula;
    }

    fields.push(toPush);
  }

  // Populate parameters from readMetadata results.
  if (readMetadataResult.extraData?.parameters) {
    for (const parameter of readMetadataResult.extraData.parameters) {
      const toPush: Parameter = {
        name: parameter.parameterCaption,
        parameterType: parameter.parameterType,
        dataType: parameter.dataType,
        value: parameter.value,
      };

      if (parameter.parameterType === 'LIST' && parameter.members) {
        toPush.members = parameter.members;
      } else if (parameter.parameterType === 'QUANTITATIVE_DATE') {
        toPush.minDate = parameter.minDate;
        toPush.maxDate = parameter.maxDate;
        toPush.periodValue = parameter.periodValue;
        toPush.periodType = parameter.periodType;
      } else if (parameter.parameterType === 'QUANTITATIVE_RANGE') {
        toPush.min = parameter.min;
        toPush.max = parameter.max;
        toPush.step = parameter.step;
      }

      simplifiedResponse.parameters.push(toPush);
    }
  }

  simplifiedResponse.fieldGroups = groupFieldsByLogicalTableId(fields);

  return simplifiedResponse;
}

export function combineFields(
  readMetadataResult: MetadataResponse,
  listFieldsResult: GraphQLResponse,
  datasourceModelResult?: DatasourceModelResponse,
): FieldsResult {
  // Create a response object that combines field data from
  // readMetadata (VizQL Data Service API) and listFields (GraphQL Metadata API) results
  // to optimize for LLM accuracy and reduce tokens in response.
  const publishedDatasource = listFieldsResult.data.publishedDatasources?.[0];
  const combinedFields: FieldsResult = {
    datasourceDescription: publishedDatasource?.description ?? '',
    ...(datasourceModelResult
      ? { datasourceModel: getSimplifiedDatasourceModel(datasourceModelResult) }
      : {}),
    fieldGroups: [],
    parameters: [],
  };
  const fields: Field[] = [];

  if (!readMetadataResult.data) {
    if (publishedDatasource?.fields.length) {
      // fallback to returning listFields results if we don't have any fields from readMetadata.
      for (const field of publishedDatasource.fields) {
        const toPush: Field = {
          name: field.name,
          logicalTableId: null,
        };
        if (field.dataType) {
          toPush.dataType = field.dataType;
        }
        if (field.aggregation) {
          toPush.defaultAggregation = field.aggregation;
        }
        populateFieldWithAdditionalProperties(field, toPush);
        fields.push(toPush);
      }
    }

    combinedFields.fieldGroups = groupFieldsByLogicalTableId(fields);
    return combinedFields;
  }

  // Only include fields from readMetadata results in our response object since only those can be used in queries.
  for (const field of readMetadataResult.data) {
    // only keeping essential field properties.
    const toPush: Field = {
      name: field.fieldCaption,
      dataType: field.dataType,
      columnClass: field.columnClass,
      logicalTableId: normalizeLogicalTableId(field.logicalTableId),
    };

    if (field.defaultAggregation) {
      toPush.defaultAggregation = field.defaultAggregation;
    }

    if (field.formula) {
      toPush.formula = field.formula;
    }

    fields.push(toPush);
  }

  // Populate parameters from readMetadata results.
  if (readMetadataResult.extraData?.parameters) {
    for (const parameter of readMetadataResult.extraData.parameters) {
      const toPush: Parameter = {
        name: parameter.parameterCaption,
        parameterType: parameter.parameterType,
        dataType: parameter.dataType,
        value: parameter.value,
      };

      if (parameter.parameterType === 'LIST' && parameter.members) {
        toPush.members = parameter.members;
      } else if (parameter.parameterType === 'QUANTITATIVE_DATE') {
        toPush.minDate = parameter.minDate;
        toPush.maxDate = parameter.maxDate;
        toPush.periodValue = parameter.periodValue;
        toPush.periodType = parameter.periodType;
      } else if (parameter.parameterType === 'QUANTITATIVE_RANGE') {
        toPush.min = parameter.min;
        toPush.max = parameter.max;
        toPush.step = parameter.step;
      }

      combinedFields.parameters.push(toPush);
    }
  }

  if (!publishedDatasource?.fields.length) {
    combinedFields.fieldGroups = groupFieldsByLogicalTableId(fields);
    return combinedFields;
  }

  // Of the fields in our response object, populate them with additional properties we get from listFields results.
  for (const field of fields) {
    const matchingListField = publishedDatasource.fields.find((f) => f.name === field.name);
    if (matchingListField) {
      populateFieldWithAdditionalProperties(matchingListField, field);
    }
  }

  combinedFields.fieldGroups = groupFieldsByLogicalTableId(fields);

  return combinedFields;
}

function populateFieldWithAdditionalProperties(sourceField: Field, targetField: Field): void {
  if (sourceField.description) {
    targetField.description = sourceField.description;
  }
  if (sourceField.descriptionInherited?.length) {
    targetField.descriptionInherited = sourceField.descriptionInherited;
  }
  if (sourceField.dataCategory) {
    targetField.dataCategory = sourceField.dataCategory;
  }
  if (sourceField.role) {
    targetField.role = sourceField.role;
  }
  if (sourceField.defaultFormat) {
    targetField.defaultFormat = sourceField.defaultFormat;
  }
  if (sourceField.formula) {
    targetField.formula = sourceField.formula;
    // Possibly null or undefined, only populate if true or false.
    if (sourceField.isAutoGenerated != undefined) {
      targetField.isAutoGenerated = sourceField.isAutoGenerated;
    }
    // Possibly null or undefined, only populate if true or false.
    if (sourceField.hasUserReference != undefined) {
      targetField.hasUserReference = sourceField.hasUserReference;
    }
  }
  // Possibly null or undefined, only populate if defined.
  if (sourceField.binSize != undefined) {
    targetField.binSize = sourceField.binSize;
  }
}

function groupFieldsByLogicalTableId(fields: Field[]): LogicalTableGroup[] {
  const groupedFields = new Map<string | null, Field[]>();

  for (const field of fields) {
    const groupKey = field.logicalTableId ?? null;
    const existingGroup = groupedFields.get(groupKey) ?? [];
    existingGroup.push(field);
    groupedFields.set(groupKey, existingGroup);
  }

  return [...groupedFields.entries()].map(([logicalTableId, fields]) => ({
    logicalTableId,
    fields,
  }));
}

function normalizeLogicalTableId(logicalTableId?: string | null): string | null {
  if (logicalTableId == undefined) {
    return null;
  }

  const normalizedLogicalTableId = logicalTableId.trim();
  return normalizedLogicalTableId.length > 0 ? normalizedLogicalTableId : null;
}

function getSimplifiedDatasourceModel(
  datasourceModelResult: DatasourceModelResponse,
): z.infer<typeof datasourceModelSchema> {
  return {
    logicalTables: datasourceModelResult.logicalTables,
    logicalTableRelationships: datasourceModelResult.logicalTableRelationships,
  };
}
